/**
 * SparkEffect Component
 * Animated spark particles that appear when avatar is selected
 * 
 * Testing Notes:
 * Testing React Native Animated API can be challenging. Recommended approach:
 * - Use vi.useFakeTimers() (or jest.useFakeTimers() if using Jest) to control time
 * - Test that animations start/trigger (verify Animated.parallel().start() is called)
 * - Test particle count and initial state setup
 * - Avoid testing frame-by-frame animation values (they're implementation details)
 * 
 * Example test structure:
 * ```typescript
 * vi.useFakeTimers();
 * const { getByTestId } = render(<SparkEffect isActive={true} />);
 * // Verify particles are rendered
 * // Verify Animated.parallel().start() is called when active changes
 * vi.useRealTimers();
 * ```
 */

import React, { useEffect, useRef } from 'react';
import { Animated, View, Platform } from 'react-native';
import { sparkEffectStyles as styles } from './SparkEffect.styles';

const DEFAULT_PARTICLE_COUNT = 12;

interface SparkEffectProps {
  /** Whether to show the spark effect */
  isActive: boolean;
  /** Number of spark particles */
  particleCount?: number;
  /** Test ID for testing */
  testID?: string;
}

/**
 * SparkEffect Component
 * Creates animated spark particles that radiate outward
 */
export const SparkEffect: React.FC<SparkEffectProps> = ({
  isActive,
  particleCount = DEFAULT_PARTICLE_COUNT,
  testID,
}) => {
  const particles = useRef(
    Array.from({ length: particleCount }, () => ({
      opacity: new Animated.Value(0),
      scale: new Animated.Value(0),
      translateX: new Animated.Value(0),
      translateY: new Animated.Value(0),
    }))
  ).current;

  useEffect(() => {
    if (isActive) {
      // Reset all particles
      particles.forEach((particle) => {
        particle.opacity.setValue(0);
        particle.scale.setValue(0);
        particle.translateX.setValue(0);
        particle.translateY.setValue(0);
      });

      // Animate all particles simultaneously
      const animations = particles.map((particle, index) => {
        // Calculate angle for each particle (distribute evenly in a circle)
        const angle = (index / particleCount) * Math.PI * 2;
        const distance = 40 + Math.random() * 20; // Random distance between 40-60
        const translateX = Math.cos(angle) * distance;
        const translateY = Math.sin(angle) * distance;

        return Animated.parallel([
          Animated.sequence([
            Animated.timing(particle.opacity, {
              toValue: 1,
              duration: 100,
              useNativeDriver: Platform.OS !== 'web',
            }),
            Animated.timing(particle.opacity, {
              toValue: 0,
              duration: 400,
              useNativeDriver: Platform.OS !== 'web',
            }),
          ]),
          Animated.sequence([
            Animated.spring(particle.scale, {
              toValue: 1.2,
              tension: 50,
              friction: 3,
              useNativeDriver: Platform.OS !== 'web',
            }),
            Animated.timing(particle.scale, {
              toValue: 0,
              duration: 300,
              useNativeDriver: Platform.OS !== 'web',
            }),
          ]),
          Animated.timing(particle.translateX, {
            toValue: translateX,
            duration: 500,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(particle.translateY, {
            toValue: translateY,
            duration: 500,
            useNativeDriver: Platform.OS !== 'web',
          }),
        ]);
      });

      Animated.parallel(animations).start();
    }
  }, [isActive, particles, particleCount]);

  if (!isActive) {
    return null;
  }

  return (
    <View style={styles.container} pointerEvents="none" testID={testID || 'spark-effect'}>
      {particles.map((particle, index) => (
        <Animated.View
          key={index}
          style={[
            styles.spark,
            {
              opacity: particle.opacity,
              transform: [
                { translateX: particle.translateX },
                { translateY: particle.translateY },
                { scale: particle.scale },
              ],
            },
          ]}
        />
      ))}
    </View>
  );
};

